// 7. HOOK POUR LA GÉNÉRATION PDF
// hooks/usePDFGeneration.ts
import { useCallback } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from 'react-hot-toast';

export const usePDFGeneration = () => {
  const handlePrint = useCallback(
    (_type: string, elementRef: React.RefObject<HTMLDivElement | null>) => {
      const element = elementRef.current;
      if (!element) {
        console.error(`Élément pour l'impression non trouvé`);
        toast.error(`Erreur : Élément pour l'impression non trouvé`);
        return;
      }

      // Créer une nouvelle fenêtre pour l'impression
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        console.error('Impossible d\'ouvrir la fenêtre d\'impression');
        toast.error('Veuillez autoriser les fenêtres popup pour cette action');
        return;
      }

      // Trouver le contenu principal (exclure les boutons d'action)
      const contentElement = element.querySelector('.prescription-content') ||
        element.querySelector('.analyses-content') ||
        element;

      if (!contentElement) {
        console.error('Contenu principal non trouvé');
        toast.error('Erreur : Impossible de trouver le contenu à imprimer');
        return;
      }

      // Créer une copie du contenu pour la modification
      const printContent = contentElement.cloneNode(true) as HTMLElement;

      // Supprimer les éléments non désirés
      const elementsToRemove = printContent.querySelectorAll(
        '.print-hidden, .no-print, button, [class*="action"], [class*="Action"]'
      );

      elementsToRemove.forEach(el => el.remove());

      // Créer le contenu HTML pour l'impression
      printWindow.document.open();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${_type === 'analyses' ? 'Demande d\'analyses' : 'Ordonnance médicale'}</title>
            <style>
              @page { 
                size: A5;
                margin: 0;
              }
              body { 
                margin: 0;
                padding: 10mm;
                font-family: Arial, sans-serif;
              }
              /* Cibler et masquer les éléments non désirés */
              .print-hidden, 
              .no-print, 
              button, 
              [class*="action"], 
              [class*="Action"] {
                display: none !important;
              }
              /* Réduire l'espace en bas de la page */
              @media print {
                @page { 
                  margin-bottom: 0; 
                }
                body { 
                  margin-bottom: 0;
                  padding-bottom: 5mm;
                }
              }
            </style>
          </head>
          <body>
            <div style="width: 100%; max-width: 100%;">
              ${printContent.outerHTML}
            </div>
            <script>
              // Imprimer automatiquement quand la page est chargée
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  // Fermer la fenêtre après l'impression
                  setTimeout(function() { 
                    window.close(); 
                  }, 100);
                }, 200);
              };
              // Gestion du bouton annuler d'impression
              window.onafterprint = function() {
                window.close();
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    },
    []
  );

  const handleDownloadPDF = useCallback(
    async (
      type: "analyses" | "prescription",
      elementRef: React.RefObject<HTMLDivElement | null>,
      patientName: string
    ) => {
      const element = elementRef.current;
      if (!element) {
        console.error(`Élément ${type}-content non trouvé`);
        toast.error(`Erreur : Élément de ${type} non trouvé`);
        return;
      }

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const canvas = await html2canvas(element, {
          scale: 1.5,
          useCORS: false,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
          foreignObjectRendering: false,
          imageTimeout: 0,
          removeContainer: true,
          ignoreElements: (el) =>
            el.classList?.contains("print-hidden") ||
            el.tagName === "SCRIPT" ||
            el.tagName === "STYLE",
        });

        if (!canvas || canvas.width === 0 || canvas.height === 0) {
          throw new Error("Canvas vide ou invalide");
        }

        const imgData = canvas.toDataURL("image/jpeg", 0.95);

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const imgWidth = pageWidth - 16;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const x = (pageWidth - imgWidth) / 2;
        const y = Math.min(8, (pageHeight - imgHeight) / 2);

        pdf.addImage(imgData, "JPEG", x, y, imgWidth, imgHeight);

        const sanitizedPatientName = patientName.replace(/[^a-zA-Z0-9]/g, "_");
        const fileName = `${type === "analyses" ? "demande_analyses" : "ordonnance"
          }_${sanitizedPatientName}_${Date.now()}.pdf`;

        pdf.save(fileName);
      } catch (error) {
        console.error(
          `Erreur détaillée lors de la génération du PDF (${type}):`,
          error
        );

        let errorMessage = `Erreur lors de la génération du PDF pour ${type}. `;

        if (error instanceof Error) {
          if (error.message.includes("oklch") || error.message.includes("color")) {
            errorMessage +=
              "Problème de couleur détecté. Veuillez essayer l'impression classique (Ctrl+P).";
          } else if (
            error.message.includes("cross-origin") ||
            error.message.includes("CORS")
          ) {
            errorMessage +=
              "Problème de sécurité du navigateur. Veuillez désactiver temporairement les extensions ou essayer un autre navigateur.";
          } else {
            errorMessage +=
              "Veuillez réessayer ou utiliser l'impression classique.";
          }
        } else {
          errorMessage += "Erreur inconnue.";
        }

        toast.error(errorMessage);
      }
    },
    []
  );

  return {
    handlePrint,
    handleDownloadPDF,
  };
};